import ReactGA from 'react-ga4';
import { UTMParameters } from '../types/analytics';

export const logEvent = (eventName: string, parameters: Record<string, any>) => {
  // Log to console in development for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics Event]: ${eventName}`, JSON.stringify(parameters, null, 2));
  }

  // Send to Google Analytics using ReactGA
  // We use user_uuid instead of email to avoid PII redaction in GA4
  // This preserves user tracking while maintaining privacy compliance
  ReactGA.event(eventName, parameters);
};

// Event types based on the Android/iOS analytics reference
export const AnalyticsEvents = {
  HOME_OPENED: 'home_opened',
  AREAS_LIST_OPEN: 'areas_list_open',
  AREA_DETAIL_OPEN: 'area_detail_open',
  TASKS_LIST_OPEN: 'tasks_list_open',
  CHAT_VIEW_OPEN: 'chat_view_open',
  SETTINGS_OPEN: 'settings_open',
  AGENT_DASHBOARD_OPEN: 'agent_dashboard_open',
  AGENT_CLIENT_LIST_OPEN: 'agent_client_list_open',
  AGENT_CLIENT_HOMES_OPEN: 'agent_client_homes_open',
  AGENT_CLIENT_HOME_WALKTHROUGH_OPEN: 'agent_client_home_walkthrough_open',
  IMAGE_ENGAGEMENT_TIME: 'image_engagement_time',
  // PWA Install Events
  PWA_INSTALL_PROMPT_SHOWN: 'pwa_install_prompt_shown',
  PWA_INSTALL_CLICKED: 'pwa_install_clicked',
  PWA_INSTALL_DISMISSED: 'pwa_install_dismissed',
  APP_STORE_REDIRECT: 'app_store_redirect',
  // Deprecated - now using engagement time tracking instead
  // IMAGE_OPENED: 'image_opened',
} as const;

// Type-safe event logging functions
export const analytics = {
  logHomeOpened: (userUuid: string, homeId: string) => {
    logEvent(AnalyticsEvents.HOME_OPENED, {
      user_uuid: userUuid,
      home_id: homeId || '',
    });
  },

  logAreasListOpen: (userUuid: string, homeId: string, areaCount: number) => {
    logEvent(AnalyticsEvents.AREAS_LIST_OPEN, {
      user_uuid: userUuid,
      home_id: homeId || '',
      area_count: areaCount,
    });
  },

  logAreaDetailOpen: (userUuid: string, areaName: string, homeId: string) => {
    logEvent(AnalyticsEvents.AREA_DETAIL_OPEN, {
      user_uuid: userUuid,
      area_name: areaName,
      home_id: homeId || '',
    });
  },

  logTasksListOpen: (userUuid: string, homeId: string) => {
    logEvent(AnalyticsEvents.TASKS_LIST_OPEN, {
      user_uuid: userUuid,
      home_id: homeId || '',
    });
  },

  logChatViewOpen: (userUuid: string, homeId: string) => {
    logEvent(AnalyticsEvents.CHAT_VIEW_OPEN, {
      user_uuid: userUuid,
      home_id: homeId || '',
    });
  },

  logSettingsOpen: (userUuid: string) => {
    logEvent(AnalyticsEvents.SETTINGS_OPEN, {
      user_uuid: userUuid,
    });
  },

  logAgentDashboardOpen: (userUuid: string, businessId: string | null, businessName: string | null) => {
    logEvent(AnalyticsEvents.AGENT_DASHBOARD_OPEN, {
      user_uuid: userUuid,
      business_id: businessId || '',
      business_name: businessName || '',
    });
  },

  logAgentClientListOpen: (userUuid: string, totalClients: number, businessId: string | null, businessName: string | null) => {
    logEvent(AnalyticsEvents.AGENT_CLIENT_LIST_OPEN, {
      user_uuid: userUuid,
      total_clients: totalClients,
      business_id: businessId || '',
      business_name: businessName || '',
    });
  },

  logAgentClientHomesOpen: (businessId: string, agentUuid: string, clientUuid: string, totalHomes: number) => {
    logEvent(AnalyticsEvents.AGENT_CLIENT_HOMES_OPEN, {
      business_id: businessId,
      agent_uuid: agentUuid,
      client_uuid: clientUuid,
      total_homes: totalHomes,
    });
  },

  logAgentClientHomeWalkthroughOpen: (source: string, homeId: string) => {
    logEvent(AnalyticsEvents.AGENT_CLIENT_HOME_WALKTHROUGH_OPEN, {
      source: source,
      home_id: homeId,
    });
  },

  logImageEngagementTime: (
    userUuid: string,
    homeId: string | null,
    imageId: string,
    cfUuid: string | null,
    engagementTimeMs: number,
    dismissalReason: string // "closed_carousel", "next_image", "tab_change", "page_unload"
  ) => {
    logEvent(AnalyticsEvents.IMAGE_ENGAGEMENT_TIME, {
      user_uuid: userUuid,
      home_id: homeId || '',
      image_id: imageId,
      cf_uuid: cfUuid || '',
      engagement_time_ms: engagementTimeMs,
      dismissal_reason: dismissalReason,
    });
  },

  // PWA Install tracking
  logPWAInstallPromptShown: (platform: 'ios' | 'android' | 'desktop', promptType: 'automatic' | 'manual') => {
    logEvent(AnalyticsEvents.PWA_INSTALL_PROMPT_SHOWN, {
      platform,
      prompt_type: promptType,
    });
  },

  logPWAInstallClicked: (platform: 'ios' | 'android' | 'desktop', source: 'prompt' | 'button') => {
    logEvent(AnalyticsEvents.PWA_INSTALL_CLICKED, {
      platform,
      source,
    });
  },

  logPWAInstallDismissed: (platform: 'ios' | 'android' | 'desktop', dismissType: 'temporary' | 'permanent') => {
    logEvent(AnalyticsEvents.PWA_INSTALL_DISMISSED, {
      platform,
      dismiss_type: dismissType,
    });
  },

  logAppStoreRedirect: (platform: 'ios' | 'android', utmParams?: UTMParameters) => {
    logEvent(AnalyticsEvents.APP_STORE_REDIRECT, {
      platform,
      store: platform === 'ios' ? 'apple_app_store' : 'google_play_store',
      ...utmParams, // Include UTM parameters if provided
    });
  },

  // Deprecated - now using engagement time tracking instead
  // logImageOpened: (userUuid: string, homeUuid: string | null, areaName: string | null, imageUuid: string) => {
  //   logEvent(AnalyticsEvents.IMAGE_OPENED, {
  //     user_uuid: userUuid,
  //     home_uuid: homeUuid || 'unknown',
  //     area_name: areaName || 'unknown',
  //     image_uuid: imageUuid,
  //   });
  // },
};